/**
 * Correlation ids for the browser side of the observability spine.
 *
 * - session_id: one stable id per browser tab (persisted in sessionStorage). It is the
 *   join key that threads PostHog ⇄ Sentry ⇄ Axiom ⇄ BetterStack for a single user session.
 * - request_id: a fresh id per outbound backend call, so one specific request can be traced
 *   from the browser through the api/edge logs.
 *
 * Pure and env-free so it is trivially unit-testable and safe to import anywhere.
 */
const KEY = 'adc_session_id';

function newId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Stable per-tab session id. Falls back to an ephemeral id if sessionStorage is unavailable. */
export function getSessionId(): string {
  if (typeof sessionStorage === 'undefined') return newId();
  try {
    let id = sessionStorage.getItem(KEY);
    if (!id) {
      id = newId();
      sessionStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    // Private mode / storage disabled — degrade to an ephemeral id rather than throw.
    return newId();
  }
}

/** Fresh id for a single outbound request. */
export function newRequestId(): string {
  return newId();
}

/** Merge the trace headers onto an existing HeadersInit (a new request id each call). */
export function withTraceHeaders(headers: HeadersInit = {}): HeadersInit {
  return { ...headers, 'X-Request-Id': newRequestId(), 'X-Session-Id': getSessionId() };
}
